import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataGrid,
  Input,
  Spinner,
  useDataGridColumnsState,
} from '@ibsys/kit';
import type { DataGridLeafColumn } from '@ibsys/kit';
import { QualityDetailItem } from '@/entities/dashboards';
import { EMPTY_RESULT } from '@/entities/expert-report';
import type { DrillDetailsPages } from '../model/useDrillDetailsPages';
import { StatBox } from './parts/StatBox';
import { fmt, getBlockResult, getQualityResultText, getTotalResult, scoreClass, totalScoreClass } from './utils';

type Props = {
  /** Список звонков ячейки окнами выдачи и итоги всей ячейки. */
  pages: DrillDetailsPages;
  blockNames: string[];
  drill: { consultant: string; period: string } | null;
  /** Открытый звонок; `null` — карточка оценки не открыта. */
  selectedCallId: number | null;
  scoreFrom: string;
  scoreTo: string;
  onChangeScoreFrom: (v: string) => void;
  onChangeScoreTo: (v: string) => void;
  onResetScore: () => void;
  onSelectCall: (call: QualityDetailItem) => void;
};

/**
 * Карточка drill-down: список звонков выбранной ячейки heatmap
 * (консультант × период) с дополнительной фильтрацией по диапазону баллов.
 *
 * Ячейку задают параметры запроса — консультант, период и границы балла, — поэтому строки
 * показываются как есть: клиентской дофильтрации у них нет. Прокрутка доводит до последнего
 * звонка ячейки: строки приходят окнами по мере прокрутки, а итоги над списком описывают всю
 * ячейку, а не показанное окно.
 */
export const PeriodDetailsCard: FC<Props> = ({
  pages,
  blockNames,
  drill,
  selectedCallId,
  scoreFrom,
  scoreTo,
  onChangeScoreFrom,
  onChangeScoreTo,
  onResetScore,
  onSelectCall,
}) => {
  const { t } = useTranslation('qualityDashboard');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('periodDetails.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScoreRangeFilter
          scoreFrom={scoreFrom}
          scoreTo={scoreTo}
          onChangeScoreFrom={onChangeScoreFrom}
          onChangeScoreTo={onChangeScoreTo}
          onResetScore={onResetScore}
        />

        <DrillContent
          pages={pages}
          blockNames={blockNames}
          drill={drill}
          selectedCallId={selectedCallId}
          onSelectCall={onSelectCall}
        />
      </CardContent>
    </Card>
  );
};

// ============ Вспомогательные секции ============

type DrillContentProps = {
  pages: DrillDetailsPages;
  blockNames: string[];
  drill: { consultant: string; period: string } | null;
  selectedCallId: number | null;
  onSelectCall: (call: QualityDetailItem) => void;
};

/** Содержимое карточки под фильтром баллов: подсказка, ожидание, пустая ячейка либо её список. */
const DrillContent: FC<DrillContentProps> = ({ pages, blockNames, drill, selectedCallId, onSelectCall }) => {
  const { t } = useTranslation('qualityDashboard');

  if (!drill) return <div className="text-muted-foreground text-sm">{t('periodDetails.hint')}</div>;

  if (pages.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (pages.totalCount === 0) {
    return <div className="text-muted-foreground text-sm">{t('periodDetails.noDataInCell')}</div>;
  }

  return (
    <>
      {pages.stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <StatBox label={t('periodDetails.stats.calls')} value={pages.stats.count} />
          <StatBox label={t('periodDetails.stats.avgScore')} value={fmt(pages.stats.avg)} />
          <StatBox
            label={t('periodDetails.stats.minMax')}
            value={`${fmt(pages.stats.min)} / ${fmt(pages.stats.max)}`}
          />
        </div>
      )}
      {/* Пересоздаём таблицу на смену набора блоков и на смену отбора: у нового отбора свой набор
          строк, и досталась бы ему выдача прежнего */}
      <DrillTable
        key={`${pages.cacheVersion}|${blockNames.join(',')}`}
        totalCount={pages.totalCount}
        pageSize={pages.pageSize}
        fetchPage={pages.fetchPage}
        blockNames={blockNames}
        selectedCallId={selectedCallId}
        onSelectCall={onSelectCall}
      />
    </>
  );
};

type ScoreRangeFilterProps = {
  scoreFrom: string;
  scoreTo: string;
  onChangeScoreFrom: (v: string) => void;
  onChangeScoreTo: (v: string) => void;
  onResetScore: () => void;
};

const ScoreRangeFilter: FC<ScoreRangeFilterProps> = ({
  scoreFrom,
  scoreTo,
  onChangeScoreFrom,
  onChangeScoreTo,
  onResetScore,
}) => {
  const { t } = useTranslation('qualityDashboard');
  return (
    <div className="flex gap-3 items-end flex-wrap mb-4">
      <Input
        type="number"
        label={t('periodDetails.scoreFrom')}
        value={scoreFrom}
        onChange={(e) => onChangeScoreFrom(e.target.value)}
        placeholder={t('periodDetails.scoreFromPlaceholder')}
        className="w-32"
      />
      <Input
        type="number"
        label={t('periodDetails.scoreTo')}
        value={scoreTo}
        onChange={(e) => onChangeScoreTo(e.target.value)}
        placeholder={t('periodDetails.scoreToPlaceholder')}
        className="w-32"
      />
      <Button variant="secondary" size="sm" onClick={onResetScore}>
        {t('periodDetails.reset')}
      </Button>
    </div>
  );
};

type DrillTableProps = {
  /** Число звонков всей ячейки — им рассчитана прокрутка */
  totalCount: number;
  /** Размер окна выдачи */
  pageSize: number;
  /** Загрузка одного окна выдачи */
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<QualityDetailItem[]>;
  blockNames: string[];
  selectedCallId: number | null;
  onSelectCall: (call: QualityDetailItem) => void;
};

const DrillTable: FC<DrillTableProps> = ({
  totalCount,
  pageSize,
  fetchPage,
  blockNames,
  selectedCallId,
  onSelectCall,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const columns = useMemo((): DataGridLeafColumn<QualityDetailItem>[] => {
    const fixed: DataGridLeafColumn<QualityDetailItem>[] = [
      {
        id: 'date',
        header: t('periodDetails.columns.date'),
        accessor: 'evaluationDate',
        size: 120,
        cell: ({ value }) => (value as string | null) ?? '—',
      },
      {
        id: 'client',
        header: t('periodDetails.columns.client'),
        accessor: 'clientName',
        size: 180,
        cell: ({ value }) => (value as string) || '—',
      },
      {
        id: 'externalId',
        header: t('periodDetails.columns.externalId'),
        accessor: 'externalId',
        size: 120,
        cell: ({ value }) => (value as string) || '—',
      },
      {
        id: 'totalScore',
        header: t('periodDetails.columns.totalScore'),
        // Итог показывается таким, каким его дало правило расчёта: балл — баллом, значение —
        // значением, набор значений — перечислением
        accessor: (row) => getQualityResultText(getTotalResult(row)) ?? EMPTY_RESULT,
        size: 110,
        align: 'center',
        className: 'font-bold',
        // Зона качества есть только у балла: итог-значение и итог-набор долей от максимума
        // не измеряются
        cellClassName: ({ row }) => totalScoreClass(row.totalRating),
        cell: ({ row }) => getQualityResultText(getTotalResult(row)) ?? EMPTY_RESULT,
      },
    ];

    return [
      ...fixed,
      ...blockNames.map((n): DataGridLeafColumn<QualityDetailItem> => {
        const findBlock = (row: QualityDetailItem) => row.blocks.find((b) => b.blockName === n);
        const getResultText = (row: QualityDetailItem): string =>
          getQualityResultText(getBlockResult(findBlock(row))) ?? EMPTY_RESULT;
        return {
          id: `block_${n}`,
          header: n,
          // Результат критерия показывается таким, каким его дало правило расчёта: балл — баллом,
          // значение — значением, набор значений — перечислением
          accessor: (row) => getResultText(row),
          size: 110,
          align: 'center',
          className: 'font-bold',
          // Зона качества есть только у балла: значение и набор долей от максимума не измеряются
          cellClassName: ({ row }) => scoreClass(findBlock(row)?.rating ?? null),
          cell: ({ row }) => getResultText(row),
        };
      }),
    ];
  }, [t, blockNames]);

  const { gridProps, panelProps } = useDataGridColumnsState({
    defaultOrder: columns.map((c) => c.id),
    defaultPinning: { left: ['date', 'client'], right: [] },
  });

  return (
    <DataGrid
      {...gridProps}
      columns={columns}
      dataSource={{ mode: 'lazy', totalCount, pageSize, fetchPage }}
      getRowId={(r) => String(r.id)}
      enableColumnResizing
      enableColumnReordering
      enableColumnPinning
      columnsPanel={{ showPinning: true, onReset: panelProps.onReset }}
      // activeRowId подсвечивает строку выбранного звонка (открыта детальная карточка).
      activeRowId={selectedCallId !== null ? String(selectedCallId) : null}
      onRowClick={(row) => onSelectCall(row)}
      height={384}
    />
  );
};
